import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import { applySafetyRole } from 'services/safetyRoleService'
import { getBpConnectedUsersByEmail, revokeAccessToBp, detachRoleOrganisation } from 'services/BpService'
import { ink, line, radius, surface, bp as bpToken } from 'features/dashboard/tokens'
import { peopleOptions, skippedMessage } from './safetyRoleHelpers'

const EMPTY_ORG = { name: '', contactName: '', contactEmail: '', contactPhone: '' }
const EMPTY_INVITE = { firstName: '', lastName: '', phoneNumber: '' }
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const SafetyRoleEditor = ({
  scope,
  roleKey,
  roleLabel,
  targetIds,
  targetLabel,
  bulk = false,
  bpId = null,
  state = 'missing',
  holders = [],
  candidates,
  organisations,
  onApplied,
  onCancel,
  idPrefix = 'safety-role'
}) => {
  const [busy, setBusy] = useState(false)
  const [userId, setUserId] = useState('')
  const [email, setEmail] = useState('')
  const [invite, setInvite] = useState(null)
  const [orgId, setOrgId] = useState('')
  const [newOrg, setNewOrg] = useState(null)
  const [confirmRemove, setConfirmRemove] = useState(null)

  useEffect(() => {
    candidates.ensure()
    organisations.ensure()
  }, [candidates.ensure, organisations.ensure])

  const apply = async (body, successText) => {
    setBusy(true)
    try {
      const res = await applySafetyRole(scope, {
        role: roleKey,
        ...body,
        ...(!bulk && scope.type !== 'bp' ? { buildingPassportIds: targetIds } : {})
      })
      const result = res?.data?.data || {}
      const applied = result.applied ?? 0
      const message = skippedMessage(result)
      if (applied === 0) {
        toast.info(message || 'Nothing to change.', { autoClose: 9000 })
        return false
      }
      const base = successText
        ? successText(result)
        : bulk ? `Updated ${applied} building${applied === 1 ? '' : 's'}.` : `${roleLabel} updated.`
      toast.success(message ? `${base} ${message}` : base, { autoClose: message ? 9000 : 5000 })
      setUserId('')
      setOrgId('')
      if (candidates.state === 'loaded') candidates.load()
      onApplied?.(result)
      return true
    } catch (e) {
      const errors = e?.response?.data?.errors
      const first = errors ? Object.values(errors)[0]?.[0] : null
      toast.error(first || 'Could not save that. Please try again.')
      return false
    } finally {
      setBusy(false)
    }
  }

  const nameSelected = () => apply({ action: 'assign', userId })

  const continueWithEmail = async () => {
    const value = email.trim().toLowerCase()
    if (!EMAIL_RE.test(value)) {
      toast.info('Please enter a full email address, for example name@company.co.uk.')
      return
    }
    setBusy(true)
    try {
      const res = await getBpConnectedUsersByEmail(value)
      const user = (res?.data?.data || [])[0]
      setBusy(false)
      if (user) {
        await apply({ action: 'assign', userId: user.id })
      } else {
        setInvite({ ...EMPTY_INVITE })
      }
    } catch (e) {
      setBusy(false)
      toast.error('Could not look up that email. Please try again.')
    }
  }

  const sendInvite = () => apply(
    { action: 'invite', email: email.trim().toLowerCase(), ...invite },
    result => (result.invited ? `Invitation sent. They will be the ${roleLabel} once they accept.` : `${roleLabel} updated.`)
  ).then(ok => { if (ok) { setInvite(null); setEmail('') } })

  const recordOrg = id => apply({ action: 'assign_organisation', organisationId: id })

  const createOrg = async () => {
    if (!newOrg?.name.trim()) return
    setBusy(true)
    let created
    try {
      created = await organisations.create(newOrg)
    } catch (e) {
      setBusy(false)
      toast.error('Could not save that organisation. Please try again.')
      return
    }
    setBusy(false)
    setNewOrg(null)
    setOrgId(created.id)
    await recordOrg(created.id)
  }

  const removeHolder = async holder => {
    setBusy(true)
    try {
      if (holder.type === 'organisation') {
        await detachRoleOrganisation({ bpId, role: roleKey, linkId: holder.organisationLinkId })
      } else {
        await revokeAccessToBp({ bpId, relationId: holder.relationId })
      }
      toast.success(`Removed ${holder.name} from ${roleLabel}.`)
      setConfirmRemove(null)
      if (candidates.state === 'loaded') candidates.load()
      onApplied?.({ applied: 1 })
    } catch (e) {
      toast.error('Could not remove them. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const options = peopleOptions(candidates.candidates, roleKey, targetIds)
  const removable = holders.filter(h => (h.type === 'organisation' ? h.organisationLinkId : h.relationId))

  return (
    <Panel role='group' aria-label={`Set the ${roleLabel} for ${targetLabel}`}>
      <Title>{state === 'assigned' && !bulk ? `${roleLabel} for ${targetLabel}` : `Set the ${roleLabel} for ${targetLabel}`}</Title>

      {!bulk && removable.length > 0 && (
        <Section>
          <SectionLabel as='span'>Currently holding this role</SectionLabel>
          <Holders>
            {removable.map(h => {
              const key = h.organisationLinkId || h.relationId
              const confirming = confirmRemove === key
              return (
                <Holder key={key}>
                  <HolderName>
                    {h.type === 'organisation'
                      ? <ApartmentRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />
                      : <PersonOutlineRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />}
                    {h.name}
                  </HolderName>
                  {confirming ? (
                    <ConfirmRow>
                      <ConfirmText>
                        {h.type === 'organisation'
                          ? `Remove ${h.name} from this role?`
                          : `Remove ${h.name}? They will also lose access to this building.`}
                      </ConfirmText>
                      <DangerBtn type='button' disabled={busy} onClick={() => removeHolder(h)}>Yes, remove</DangerBtn>
                      <GhostBtn type='button' disabled={busy} onClick={() => setConfirmRemove(null)}>Keep</GhostBtn>
                    </ConfirmRow>
                  ) : (
                    <GhostBtn type='button' disabled={busy} onClick={() => setConfirmRemove(key)}>Remove</GhostBtn>
                  )}
                </Holder>
              )
            })}
          </Holders>
        </Section>
      )}

      <Columns>
        <Section>
          <SectionLabel as='span'>{state === 'assigned' && !bulk ? 'Add a person with access' : 'Name a person with access'}</SectionLabel>
          {candidates.state === 'loading' && candidates.candidates.length === 0 ? (
            <SkeletonLine $w='100%' $h={44} />
          ) : (
            <Row>
              <Picker
                ariaLabel={`Person to name as ${roleLabel}`}
                value={userId}
                onChange={setUserId}
                options={options}
                placeholder={options.length ? 'Search people by name…' : 'No one else has access yet'}
                searchPlaceholder='Search by name…'
              />
              <PrimaryBtn type='button' disabled={!userId || busy} onClick={nameSelected}>Name them</PrimaryBtn>
            </Row>
          )}
          {candidates.state === 'error' && (
            <Hint>Could not load the people list. <LinkBtn type='button' onClick={candidates.load}>Try again</LinkBtn></Hint>
          )}

          <SectionLabel htmlFor={`${idPrefix}-email`}>Someone not listed? Enter their email</SectionLabel>
          <Row>
            <TextInput
              id={`${idPrefix}-email`}
              type='email'
              placeholder='Their email address'
              value={email}
              disabled={!!invite}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') continueWithEmail() }}
            />
            {!invite && (
              <SecondaryBtn type='button' disabled={busy || !email.trim()} onClick={continueWithEmail}>Continue</SecondaryBtn>
            )}
          </Row>
          {invite && (
            <InviteBox>
              <InviteLead>
                <MailOutlineRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />
                No account uses this email yet. We will send them an invitation, and they become the {roleLabel} when they accept.
              </InviteLead>
              <Fields>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-first`}>First name</SectionLabel>
                  <TextInput id={`${idPrefix}-first`} value={invite.firstName} onChange={e => setInvite(v => ({ ...v, firstName: e.target.value }))} autoFocus />
                </Field>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-last`}>Last name</SectionLabel>
                  <TextInput id={`${idPrefix}-last`} value={invite.lastName} onChange={e => setInvite(v => ({ ...v, lastName: e.target.value }))} />
                </Field>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-phone`}>Phone (optional)</SectionLabel>
                  <TextInput id={`${idPrefix}-phone`} type='tel' value={invite.phoneNumber} onChange={e => setInvite(v => ({ ...v, phoneNumber: e.target.value }))} />
                </Field>
              </Fields>
              <Row>
                <PrimaryBtn type='button' disabled={busy || !invite.firstName.trim() || !invite.lastName.trim()} onClick={sendInvite}>
                  {busy ? 'Sending…' : 'Send invitation'}
                </PrimaryBtn>
                <LinkBtn type='button' disabled={busy} onClick={() => setInvite(null)}>Use a different email</LinkBtn>
              </Row>
            </InviteBox>
          )}
        </Section>

        <Section>
          <SectionLabel as='span'>Or record an organisation</SectionLabel>
          {newOrg ? (
            <InviteBox>
              <Fields>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-org-name`}>Organisation name</SectionLabel>
                  <TextInput id={`${idPrefix}-org-name`} value={newOrg.name} placeholder='e.g. Acme Fire Safety Ltd' onChange={e => setNewOrg(v => ({ ...v, name: e.target.value }))} autoFocus />
                </Field>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-org-contact`}>Contact name (optional)</SectionLabel>
                  <TextInput id={`${idPrefix}-org-contact`} value={newOrg.contactName} onChange={e => setNewOrg(v => ({ ...v, contactName: e.target.value }))} />
                </Field>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-org-email`}>Contact email (optional)</SectionLabel>
                  <TextInput id={`${idPrefix}-org-email`} type='email' value={newOrg.contactEmail} onChange={e => setNewOrg(v => ({ ...v, contactEmail: e.target.value }))} />
                </Field>
                <Field>
                  <SectionLabel htmlFor={`${idPrefix}-org-phone`}>Contact phone (optional)</SectionLabel>
                  <TextInput id={`${idPrefix}-org-phone`} type='tel' value={newOrg.contactPhone} onChange={e => setNewOrg(v => ({ ...v, contactPhone: e.target.value }))} />
                </Field>
              </Fields>
              <Row>
                <PrimaryBtn type='button' disabled={busy || !newOrg.name.trim()} onClick={createOrg}>{busy ? 'Saving…' : 'Save and record it'}</PrimaryBtn>
                <LinkBtn type='button' disabled={busy} onClick={() => setNewOrg(null)}>Back to the list</LinkBtn>
              </Row>
            </InviteBox>
          ) : (
            <>
              {organisations.state === 'loading' && organisations.orgs.length === 0 ? (
                <SkeletonLine $w='100%' $h={44} />
              ) : (
                <Row>
                  <Picker
                    ariaLabel='Organisation'
                    value={orgId}
                    onChange={setOrgId}
                    options={organisations.options}
                    placeholder={organisations.options.length ? 'Select an organisation…' : 'No saved organisations yet'}
                    searchPlaceholder='Search organisations…'
                  />
                  <PrimaryBtn type='button' disabled={!orgId || busy} onClick={() => recordOrg(orgId)}>Record it</PrimaryBtn>
                </Row>
              )}
              {organisations.state === 'error' && (
                <Hint>Could not load your organisations. <LinkBtn type='button' onClick={organisations.load}>Try again</LinkBtn></Hint>
              )}
              <LinkBtn type='button' onClick={() => setNewOrg({ ...EMPTY_ORG })}>+ Add a new organisation</LinkBtn>
            </>
          )}
        </Section>
      </Columns>

      <Footer>
        {state === 'missing' && (
          <SecondaryBtn type='button' disabled={busy} onClick={() => apply({ action: 'not_applicable' }, () => `${roleLabel} marked as not applicable.`)}>
            {bulk ? `This role does not apply to ${targetLabel}` : 'This role does not apply to this building'}
          </SecondaryBtn>
        )}
        {state === 'na' && !bulk && (
          <SecondaryBtn type='button' disabled={busy} onClick={() => apply({ action: 'applicable' }, () => `${roleLabel} now applies to this building.`)}>
            This role does apply after all
          </SecondaryBtn>
        )}
        {onCancel && <LinkBtn type='button' disabled={busy} onClick={onCancel}>Close</LinkBtn>}
      </Footer>
    </Panel>
  )
}

export default SafetyRoleEditor

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: ${surface.canvas};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const Title = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${ink.base};
`

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: ${bpToken.mobile}) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const SectionLabel = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #344054;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Picker = styled(SearchableSelect)`
  flex: 1 1 220px;
  min-width: 0;
`

const TextInput = styled.input`
  flex: 1 1 220px;
  min-width: 0;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: #fff;
  font: inherit;
  font-size: 16px;
  color: ${ink.base};
  box-sizing: border-box;
  &:focus { outline: 2px solid #33becc; outline-offset: 0; border-color: transparent; }
  &:disabled { background: ${surface.wash}; }
`

const baseBtn = `
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.55; cursor: default; }
  &:focus-visible { outline: 2px solid #0c1229; outline-offset: 2px; }
`

const PrimaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
`

const SecondaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid ${line.strong};
  background: #fff;
  color: ${ink.base};
  &:hover:not(:disabled) { background: #f4f4f5; }
`

const GhostBtn = styled.button`
  ${baseBtn}
  min-height: 40px;
  padding: 0 14px;
  font-size: 14px;
  border: 1px solid ${line.strong};
  background: #fff;
  color: ${ink.base};
`

const DangerBtn = styled.button`
  ${baseBtn}
  min-height: 40px;
  padding: 0 14px;
  font-size: 14px;
  border: 1px solid #b00020;
  background: #fff;
  color: #b00020;
  &:hover:not(:disabled) { background: #fdecea; }
`

const LinkBtn = styled.button`
  align-self: flex-start;
  min-height: 40px;
  padding: 0 4px;
  border: none;
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  color: #17727b;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:disabled { opacity: 0.55; cursor: default; }
`

const Hint = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${ink.mute};
`

const Holders = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Holder = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
`

const HolderName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: ${ink.base};
`

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const ConfirmText = styled.span`
  font-size: 14px;
  color: #b00020;
`

const InviteBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
`

const InviteLead = styled.p`
  display: flex;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #344054;
`

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  & > input { flex: 0 0 auto; }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid ${line.base};
`
